import React from "react";
import { Col, Row, Space, Tooltip } from "antd";
import { InfoCircleFilled, SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import TablePagination from "../../../components/Table/TablePagination";
import { color, icon } from "../../../resource";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { Texts } from "../../GeneralSettingPage/orderTimeFrame/OrderPeriod.styles";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import { RangePicker } from "../../../components/DatePicker/DatePicker";
import StatusBadge from "../../../components/Badge/StatusBadge";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { CreditLimitStatusLabel } from "../../../definitions/creditLimit";
import Select from "../../../components/Select/Select";
import { dateFormatter, numberFormatter } from "../../../utility/Formatter";
import type { AllOrderCreditLimitEntity } from "../../../entities/OrderCreditEntity";
import moment from "moment";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";

interface Props {
  data: AllOrderCreditLimitEntity;
  sortField: any;
  sortDirection: any;
  handleSortChange: any;
  searchText: any;
  setSearchText: any;
  filterDate: any;
  setFilterDate: any;
  status: any;
  setStatus: any;
  page: any;
  setPage: any;
  loading: any;
  setModalCheck: (params: { open: boolean; id: string | null }) => void;
  setWaitApprove: (value: string | undefined) => void;
  waitApprove: string | undefined;
}

function OrderTab({
  data,
  sortField,
  sortDirection,
  handleSortChange,
  searchText,
  setSearchText,
  filterDate,
  setFilterDate,
  status,
  setStatus,
  page,
  setPage,
  loading,
  setModalCheck,
  waitApprove,
  setWaitApprove,
}: Props) {
  const navigate = useNavigate();
  const company = JSON.parse(localStorage.getItem("company")!);
  const columns: any = [
    {
      title: () => (
        <SortFieldCustom
          title='วันที่สั่งซื้อ'
          sortDirection={sortField === "createAt" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("createAt")}
        />
      ),
      dataIndex: "createAt",
      key: "createAt",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>{moment(value).format("DD/MM/YYYY, HH:mm")}</Texts>
            </>
          ),
        };
      },
    },
    {
      title: "รหัสคำสั่งซื้อ",
      dataIndex: "orderNo",
      key: "orderNo",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>{value}</Texts>
              {company.companyCode === "ICPL" && row.refOrderNo && (
                <Space
                  direction='horizontal'
                  size={0}
                  style={{ cursor: "pointer", display: "inline-flex", alignItems: "flex-start" }}
                >
                  <Tooltip title={`รหัสคำสั่งซื้อ: ${row.refOrderNo}`} placement='top'>
                    <InfoCircleFilled style={{ color: color.error }} />
                  </Tooltip>
                  <Texts style={{ color: color.error, marginLeft: "4px" }}>Reorder</Texts>
                </Space>
              )}
            </>
          ),
        };
      },
    },
    {
      title: "ชื่อร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      width: company.companyCode !== "ICPL" ? "30%" : "25%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>{value}</Texts>
              <Texts style={{ color: color.background4 }}>{row.taxNo}</Texts>
            </>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='วงเงินที่ขอ'
          sortDirection={sortField === "requestedOverCredit" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("requestedOverCredit")}
        />
      ),
      dataIndex: "requestedOverCredit",
      key: "requestedOverCredit",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>฿ {value ? numberFormatter(value, 2) : "0.00"}</Texts>
              <Texts style={{ color: color.background4 }}>
                {numberFormatter(row.percentOverCredit, 2) || "0.00"} %
              </Texts>
            </>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title={company.companyCode === "ICPL" ? "ยอดใบสั่งขาย" : "ยอดคำสั่งซื้อ"}
          sortDirection={sortField === "totalPrice" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("totalPrice")}
        />
      ),
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value: any, row: any, index: number) => {
        return {
          children: <Texts size='16'>฿ {numberFormatter(value, 2)}</Texts>,
        };
      },
    },
    ...(company.companyCode === "ICPL"
      ? [
          {
            title: "สิ่งที่รออนุมัติ",
            key: "approvalStatus",
            render: (_: any, row: any) => {
              const items = [
                { key: "creditStatus", label: "วงเงินเกิน", value: row.creditStatus },
                { key: "soStatus", label: "อนุมัติใบสั่งขาย", value: row.soStatus },
              ];

              const renderItems = items
                .filter((item) => item.value)
                .map((item, idx) => {
                  let iconSrc;
                  switch (item.value) {
                    case "green":
                      iconSrc = icon.check_success;
                      break;
                    case "yellow":
                      iconSrc = icon.check_wait;
                      break;
                    case "red":
                      iconSrc = icon.check_rejected;
                      break;
                    default:
                      iconSrc = icon.check_white;
                  }

                  return (
                    <Space key={idx} align='center' size={4}>
                      <img src={iconSrc} style={{ width: 20, height: 20 }} alt='' />
                      <Text fontFamily='Sarabun' fontSize={14} fontWeight={400}>
                        {item.label}
                      </Text>
                    </Space>
                  );
                });

              return (
                <Space direction='vertical' size={2}>
                  {renderItems.length ? (
                    renderItems
                  ) : (
                    <Text fontFamily='Sarabun' fontSize={14} fontWeight={400}>
                      -
                    </Text>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
    {
      title: "สถานะ",
      dataIndex: "thisStatus",
      key: "thisStatus",
      render: (value: any, row: any, index: number) => {
        const normalizedStatus = normalizeStatus(row.thisStatus);
        const meta = STATUS_CREDIT[normalizedStatus] || {};
        return {
          children: <StatusBadge label={meta.label || "-"} bgColor={meta.color || "gray"} />,
        };
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <Row gutter={10} style={{ gap: 6 }} justify='start'>
              <ActionBtn
                onClick={() => {
                  navigate(`/order/check-credit-limit/${row.orderId}`);
                }}
                icon={<UnorderedListOutlined style={{ width: 20, height: 20, fontWeight: 700 }} />}
              />
              <ActionBtn
                onClick={() => setModalCheck({ open: true, id: row.orderId })}
                icon={<img src={icon.Audit} style={{ width: 20, height: 20, fontWeight: 700 }} />}
              />
            </Row>
          ),
        };
      },
    },
  ];
  return (
    <>
      <Row gutter={8} style={{ margin: "12px 0px" }}>
        {[
          {
            key: "PENDING",
            value: "PENDING",
            label: `รออนุมัติ (${numberFormatter(data?.countPending || 0, 0)})`,
          },
          { key: "APPROVE", value: "APPROVE", label: `อนุมัติแล้ว` },
          { key: "REJECT", value: "REJECT", label: `ไม่อนุมัติ` },
        ].map(({ key, label }) => (
          <Col key={key}>
            <Button
              style={{ width: "max-content" }}
              title={label}
              typeButton={key === status ? "navy-blue" : "light-navy-blue"}
              onClick={() => setStatus(key)}
            />
          </Col>
        ))}
      </Row>
      <Row justify='space-between' gutter={[8, 8]} style={{ marginBottom: "12px" }}>
        <Col className='gutter-row' span={10}>
          <Input
            placeholder='ค้นหาชื่อร้านค้า / เลขนิติบุคคล / รหัสคำสั่งซื้อ'
            suffix={<SearchOutlined />}
            defaultValue={searchText}
            onPressEnter={(e) => {
              const value = (e.target as HTMLTextAreaElement).value;
              setSearchText(value);
              setPage(1);
            }}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              if (!value) {
                setSearchText(undefined);
                setPage(1);
              }
            }}
          />
        </Col>
        <Col className='gutter-row' span={company.companyCode === "ICPL" ? 8 : 6}>
          <Row gutter={[8, 8]}>
            <Col span={company.companyCode === "ICPL" ? 12 : 24}>
              <RangePicker
                allowEmpty={[true, true]}
                enablePast
                value={filterDate}
                onChange={(dates) => {
                  setFilterDate(dates);
                  setPage(1);
                }}
              />
            </Col>
            {company.companyCode === "ICPL" && (
              <Col className='gutter-row' span={12}>
                <Select
                  value={waitApprove}
                  onChange={(dates) => {
                    setWaitApprove(dates);
                    setPage(1);
                  }}
                  data={[
                    { key: "CREDIT", value: "CREDIT", label: "วงเงินเกิน" },
                    { key: "SO", value: "SO", label: "อนุมัติใบสั่งขาย" },
                  ]}
                  placeholder='เลือกสิ่งที่รออนุมัติ'
                />
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          marginBottom: "8px",
        }}
      >
        <Text fontSize={14}>ทั้งหมด {numberFormatter(data?.count, 0)} รายการ</Text>
      </div>
      <TablePagination
        isLoading={loading}
        columns={columns}
        data={data?.data || []}
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize: 10,
          total: data?.count || 0,
          onChange: (page) => {
            setPage(page);
          },
        }}
        onPageChange={(pageNum) => setPage(pageNum)}
        locale={{
          emptyText: (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 350,
              }}
            >
              <img
                src={icon.emptyHistoryPoint}
                style={{
                  width: 80,
                  height: 90,
                }}
                alt='empty_table'
              />
              <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                ไม่มีรายการคำสั่งซื้อ
              </Text>
            </div>
          ),
        }}
      />
    </>
  );
}

export default OrderTab;
